import React, { useRef, useState, useEffect } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { highlightHtml } from '@/lib/email/html-syntax-highlighter';
import { highlightCSS } from '@/lib/email/css-syntax-highlighter';

interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  language?: 'html' | 'css' | 'plaintext';
}

export const CodeEditor: React.FC<CodeEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter code here...',
  className = '',
  minHeight = '400px',
  language = 'html'
}) => {
  const { resolvedTheme } = useTheme();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLPreElement>(null);
  const [scrollTop, setScrollTop] = useState<number>(0);
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const [lineHeights, setLineHeights] = useState<number[]>([]);

  const isDark = resolvedTheme === 'dark';

  // Apply syntax highlighting based on language
  const highlightedHtml =
    language === 'html' ? highlightHtml(value || ' ') :
    language === 'css' ? highlightCSS(value || ' ') :
    (value || ' ');

  // Count lines
  const lines = value.split('\n');
  const lineCount = lines.length;

  // Measure actual height of each line (including wrapping)
  useEffect(() => {
    if (!mirrorRef.current || !textareaRef.current) return;

    const mirror = mirrorRef.current;
    const textarea = textareaRef.current;

    // Clear mirror
    mirror.innerHTML = '';

    // Copy exact styles from textarea
    const computedStyle = window.getComputedStyle(textarea);
    mirror.style.font = computedStyle.font;
    mirror.style.fontSize = computedStyle.fontSize;
    mirror.style.fontFamily = computedStyle.fontFamily;
    mirror.style.lineHeight = computedStyle.lineHeight;
    mirror.style.letterSpacing = computedStyle.letterSpacing;
    mirror.style.whiteSpace = computedStyle.whiteSpace;
    mirror.style.wordBreak = computedStyle.wordBreak;
    mirror.style.width = `${textarea.clientWidth - parseFloat(computedStyle.paddingLeft) - parseFloat(computedStyle.paddingRight)}px`;
    mirror.style.padding = '0';

    // Measure each line
    const heights: number[] = [];
    lines.forEach((line) => {
      const lineDiv = document.createElement('div');
      lineDiv.textContent = line || ' '; // Empty lines need space to have height
      mirror.appendChild(lineDiv);
      heights.push(lineDiv.offsetHeight);
    });

    setLineHeights(heights);
  }, [value, lines.length]);

  // Sync scroll between textarea, line numbers, and highlight layer
  const handleScroll = () => {
    if (textareaRef.current) {
      setScrollTop(textareaRef.current.scrollTop);
      setScrollLeft(textareaRef.current.scrollLeft);

      // Sync highlight layer scroll
      if (highlightRef.current) {
        highlightRef.current.scrollTop = textareaRef.current.scrollTop;
        highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
      }
    }
  };

  // Handle tab key and auto-indent on Enter
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    const start = target.selectionStart;
    const end = target.selectionEnd;

    if (e.key === 'Tab') {
      e.preventDefault();
      const newValue = value.substring(0, start) + '  ' + value.substring(end);
      onChange(newValue);

      // Restore cursor position
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = start + 2;
      }, 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();

      // Get current line
      const textBeforeStart = value.substring(0, start);
      const currentLineStart = textBeforeStart.lastIndexOf('\n') + 1;
      const currentLine = value.substring(currentLineStart, start);

      // Count leading spaces
      const leadingSpaces = currentLine.match(/^(\s*)/)?.[1] || '';

      // Insert newline with same indentation
      const newValue = value.substring(0, start) + '\n' + leadingSpaces + value.substring(end);
      onChange(newValue);

      // Move cursor to end of indentation on new line
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = start + 1 + leadingSpaces.length;
      }, 0);
    }
  };

  return (
    <>
      {/* Syntax highlighting styles */}
      <style>
        {`
          /* HTML Syntax Colors */
          .syntax-highlight .syntax-tag {
            color: ${isDark ? '#569cd6' : '#0066cc'};
            font-weight: 500;
          }

          /* Regular attributes */
          .syntax-highlight .syntax-attribute {
            color: ${isDark ? '#9cdcfe' : '#116329'};
          }

          /* Class attribute name */
          .syntax-highlight .syntax-attribute-class {
            color: ${isDark ? '#dcdcaa' : '#d4b106'};
            font-weight: 500;
          }

          /* Individual class names */
          .syntax-highlight .syntax-class-name {
            color: ${isDark ? '#4ec9b0' : '#0066cc'};
            font-weight: 500;
          }

          /* ID attribute name */
          .syntax-highlight .syntax-attribute-id {
            color: ${isDark ? '#c678dd' : '#6f42c1'};
            font-weight: 500;
          }

          /* ID values */
          .syntax-highlight .syntax-id-value {
            color: ${isDark ? '#c678dd' : '#6f42c1'};
          }

          .syntax-highlight .syntax-string {
            color: ${isDark ? '#ce9178' : '#a31515'};
          }
          .syntax-highlight .syntax-comment {
            color: ${isDark ? '#6a9955' : '#6a737d'};
            font-style: italic;
          }
          .syntax-highlight .syntax-doctype {
            color: ${isDark ? '#569cd6' : '#0066cc'};
            font-weight: 500;
          }
          .syntax-highlight .syntax-text {
            color: ${isDark ? '#d4d4d4' : '#24292e'};
          }

          /* CSS Syntax Colors */
          /* Generic selector (fallback) */
          .syntax-highlight .syntax-selector {
            color: ${isDark ? '#c678dd' : '#6f42c1'};
            font-weight: 500;
          }

          /* Class selector (.btn, .primary) */
          .syntax-highlight .syntax-selector-class {
            color: ${isDark ? '#4ec9b0' : '#0066cc'};
            font-weight: 500;
          }

          /* ID selector (#header, #main) */
          .syntax-highlight .syntax-selector-id {
            color: ${isDark ? '#c678dd' : '#6f42c1'};
            font-weight: 500;
          }

          /* Element selector (div, span, body) */
          .syntax-highlight .syntax-selector-element {
            color: ${isDark ? '#569cd6' : '#22863a'};
            font-weight: 500;
          }

          /* Pseudo-classes and pseudo-elements (:hover, ::before) */
          .syntax-highlight .syntax-selector-pseudo {
            color: ${isDark ? '#dcdcaa' : '#d4b106'};
            font-weight: 500;
          }

          /* Attribute selectors ([type="text"]) */
          .syntax-highlight .syntax-selector-attribute {
            color: ${isDark ? '#9cdcfe' : '#116329'};
          }

          /* CSS properties */
          .syntax-highlight .syntax-property {
            color: ${isDark ? '#9cdcfe' : '#0066cc'};
          }

          /* CSS values */
          .syntax-highlight .syntax-value {
            color: ${isDark ? '#ce9178' : '#a31515'};
          }

          /* Numbers with units */
          .syntax-highlight .syntax-number {
            color: ${isDark ? '#b5cea8' : '#e36209'};
          }

          /* Punctuation ({};:,) */
          .syntax-highlight .syntax-punctuation {
            color: ${isDark ? '#d4d4d4' : '#24292e'};
          }

          /* Color Values & Swatches */
          .syntax-highlight .syntax-color {
            color: ${isDark ? '#ce9178' : '#a31515'};
          }

          .syntax-highlight .color-value-wrapper {
            display: inline-flex;
            align-items: center;
            gap: 4px;
          }

          .syntax-highlight .color-swatch {
            display: inline-block;
            width: 12px;
            height: 12px;
            border: 1px solid ${isDark ? '#666' : '#ccc'};
            border-radius: 2px;
            vertical-align: middle;
            background-image:
              linear-gradient(45deg, #ccc 25%, transparent 25%),
              linear-gradient(-45deg, #ccc 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #ccc 75%),
              linear-gradient(-45deg, transparent 75%, #ccc 75%);
            background-size: 8px 8px;
            background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
          }
        `}
      </style>

      <div className={`relative rounded-md border ${className} ${
        isDark
          ? 'bg-[#1e1e1e] border-gray-700'
          : 'bg-white border-gray-300'
      }`}
      style={{ minHeight, overflow: 'hidden' }}>
        {/* Hidden mirror for measuring line heights */}
        <div
          ref={mirrorRef}
          style={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            top: 0,
            left: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        />

      {/* Syntax highlighting layer */}
      <pre
        ref={highlightRef}
        className="absolute left-0 top-0 w-full h-full overflow-auto pointer-events-none select-none"
        style={{
          margin: 0,
          paddingLeft: '2.5rem',
          paddingRight: '1rem',
          paddingTop: '0.75rem',
          paddingBottom: '0.75rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: '1.5rem',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          tabSize: 2,
        }}
        aria-hidden="true"
      >
        <code
          className="syntax-highlight"
          dangerouslySetInnerHTML={{ __html: highlightedHtml }}
        />
      </pre>

      {/* Line numbers column */}
      <div
        className="absolute left-0 top-0 bottom-0 select-none pointer-events-none z-10"
        style={{
          width: '2rem',
          overflow: 'hidden'
        }}
      >
        <div
          className={`font-mono text-xs text-right ${
            isDark ? 'text-gray-500' : 'text-gray-400'
          }`}
          style={{
            paddingTop: '0.75rem',
            paddingBottom: '0.75rem',
            paddingRight: '0.5rem',
            transform: `translateY(-${scrollTop}px)`,
            lineHeight: '1.5rem'
          }}
        >
          {lines.map((_, i) => (
            <div
              key={i}
              style={{
                height: lineHeights[i] ? `${lineHeights[i]}px` : '1.5rem',
                lineHeight: '1.5rem',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-end'
              }}
            >
              {i + 1}
            </div>
          ))}
        </div>
      </div>

      {/* Actual textarea - transparent text to show highlighting */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onScroll={handleScroll}
        placeholder={placeholder}
        className={`relative w-full font-mono text-sm leading-6 resize-none border-0 focus:outline-none focus:ring-0 z-20 ${
          isDark
            ? 'bg-transparent caret-gray-100 placeholder:text-gray-600'
            : 'bg-transparent caret-gray-900 placeholder:text-gray-400'
        }`}
        style={{
          minHeight,
          paddingLeft: '2.5rem',
          paddingRight: '1rem',
          paddingTop: '0.75rem',
          paddingBottom: '0.75rem',
          tabSize: 2,
          lineHeight: '1.5rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: 'transparent',
          caretColor: isDark ? '#f3f4f6' : '#111827',
        }}
        spellCheck={false}
      />
      </div>
    </>
  );
};
